import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  getDefaultAppState,
  clearAppStateForLocalStorage,
} from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useCallbackRefState } from "@excalidraw/excalidraw/hooks/useCallbackRefState";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import {
  isElementLink,
  clearElementsForLocalStorage,
} from "@excalidraw/element";
import { restore, restoreAppState } from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import { sortFramesByPresentationOrder } from "@excalidraw/element/frame";

import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { ExcalidrawFrameLikeElement } from "@excalidraw/element/types";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import { DuplicationHandler } from "./presentation/DuplicationHandler";
import { Presentation } from "./presentation/Presentation";
import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  isPWAMode,
  SYNC_BROWSER_TABS_TIMEOUT,
  generateUniqueSessionId,
  STORAGE_KEYS,
  getSessionStorageKey,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import {
  importFromLocalStorageWithFileHandle,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";
import { sessionManager } from "./data/SessionManager";

import "./index.scss";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }

  interface LaunchParams {
    files?: FileSystemHandle[];
    targetURL?: string;
  }

  interface LaunchQueue {
    setConsumer(consumer: (launchParams: LaunchParams) => void): void;
  }

  interface Window {
    launchQueue?: LaunchQueue;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = await importFromLocalStorageWithFileHandle();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();
  const [presentationData, setPresentationData] = useState<{
    enabled: boolean;
    elements: readonly NonDeletedExcalidrawElement[];
    appState: AppState;
    frameIndex: number;
    // Save original scene data to restore after exiting presentation
    originalElements: readonly OrderedExcalidrawElement[];
    originalAppState: AppState;
    originalFiles: BinaryFiles;
  } | null>(null);

  // Initialize session manager early in the component lifecycle
  useEffect(() => {
    // Session manager is already initialized as a singleton
    // This just ensures it's loaded and ready
    sessionManager.getSessionId();
  }, []);

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    // Allow installing libraries from any URL (not just excalidraw.com), so
    // third-party libraries can be imported online via `#addLibrary=<url>`.
    validateLibraryUrl: () => true,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  // Preload bundled default libraries (e.g. stick figures) once per browser so
  // they show up in the library panel out of the box. `merge: true` keeps them
  // alongside whatever the IndexedDB adapter loads (see `useHandleLibrary`).
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const PRELOADED_LIBRARIES_KEY = "excalidraw-preloaded-default-libraries";
    if (localStorage.getItem(PRELOADED_LIBRARIES_KEY)) {
      return;
    }
    (async () => {
      try {
        const response = await fetch("/libraries/stick-figures.excalidrawlib");
        if (!response.ok) {
          return;
        }
        await excalidrawAPI.updateLibrary({
          libraryItems: await response.blob(),
          merge: true,
          defaultStatus: "published",
          openLibraryMenu: false,
        });
        localStorage.setItem(PRELOADED_LIBRARIES_KEY, "1");
      } catch (error) {
        console.warn("Failed to preload default libraries", error);
      }
    })();
  }, [excalidrawAPI]);

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    // 将 excalidrawAPI 设置到 window 对象上，供其他组件使用
    if (excalidrawAPI) {
      (window as any).excalidrawAPI = excalidrawAPI;
    } else {
      (window as any).excalidrawAPI = null;
    }

    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
      setPresentationData(null);

      // Check if fileHandle needs permission and show toast
      const appState = data.scene?.appState as any;
      if (appState?.fileHandleNeedsPermission && appState?.fileHandle) {
        // Show toast to inform user about permission requirement
        setTimeout(() => {
          excalidrawAPI.setToast({
            message: "文件访问权限已过期，请按 Ctrl+S 重新授权以继续自动保存",
            duration: 8000,
            closable: true,
          });
        }, 1000);
      }
    });

    // Handle PWA file launching via launchQueue
    if ("launchQueue" in window && window.launchQueue) {
      window.launchQueue.setConsumer(async (launchParams) => {
        console.log(
          "launchQueue triggered with files:",
          launchParams.files?.length,
        );
        if (launchParams.files && launchParams.files.length > 0) {
          try {
            // Check if current window has content (elements) that would be overwritten
            const currentElements =
              excalidrawAPI.getSceneElementsIncludingDeleted();
            const hasContent = currentElements && currentElements.length > 0;

            console.log(
              "launchQueue - current window has content:",
              hasContent,
            );

            if (hasContent) {
              // If current window has content, open file in a new window
              const newSessionId = generateUniqueSessionId();
              const newUrl = new URL(
                window.location.origin + window.location.pathname,
              );
              newUrl.searchParams.set("session", newSessionId);

              console.log(
                "launchQueue - opening file in new window to preserve current content",
              );

              window.open(newUrl.toString(), "_blank");

              // The new window will also get the launchQueue event and handle the file
              // since it's the same application instance
              return;
            }

            // If current window is empty, ensure it has a unique session ID for isolation
            const urlParams = new URLSearchParams(window.location.search);
            if (!urlParams.get("session")) {
              const newSessionId = generateUniqueSessionId();
              urlParams.set("session", newSessionId);
              const newUrl = `${
                window.location.pathname
              }?${urlParams.toString()}${window.location.hash}`;
              window.history.replaceState({}, "", newUrl);
              console.log(
                "launchQueue - assigned unique session to current window:",
                newSessionId,
              );
            }

            console.log(
              "launchQueue - loading file in current window with isolated storage",
            );

            const fileHandle = launchParams.files[0] as FileSystemFileHandle;
            const file = await fileHandle.getFile();
            const { elements, appState, files } = await loadFromBlob(
              file,
              null,
              null,
            );

            // Update the scene with new content and file handle
            // 优先使用文件系统的文件名而不是文件内容中保存的名称
            const fileSystemName = file.name.replace(/\.excalidraw$/, "");
            excalidrawAPI.updateScene({
              elements: elements || [],
              appState: {
                ...appState,
                fileHandle: fileHandle as any,
                name: fileSystemName,
              },
            });

            // Add any associated files (images, etc.)
            if (files) {
              excalidrawAPI.addFiles(Object.values(files));
            }
          } catch (error) {
            console.error("Failed to handle launched file:", error);
          }
        }
      });
    }

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          // Clear presentation data when loading new scene
          console.log("📂 hashchange 加载新场景，清除 presentationData");
          setPresentationData(null);
          if (data.scene) {
            excalidrawAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }

      // Skip tab sync for PWA mode to prevent content overwriting between windows
      if (isPWAMode()) {
        console.log("Skipping sync for PWA mode");
        return;
      }

      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer("VERSION_DATA_STATE")) {
          // Use async function to load fileHandle as well
          importFromLocalStorageWithFileHandle().then((localDataState) => {
            const username = importUsernameFromLocalStorage();
            setLangCode(getPreferredLanguage());
            excalidrawAPI.updateScene({
              ...localDataState,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
            LibraryIndexedDBAdapter.load().then((data) => {
              if (data) {
                excalidrawAPI.updateLibrary({
                  libraryItems: data.libraryItems,
                });
              }
            });
            collabAPI?.setUsername(username || "");
          });
        }

        if (isBrowserStorageStateNewer("VERSION_FILES")) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (excalidrawAPI) {
        if (
          LocalData.fileStorage.shouldPreventUnload(
            excalidrawAPI.getSceneElements(),
          )
        ) {
          if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
            preventUnload(event);
          } else {
            console.warn(
              "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
            );
          }
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  // Monitor fullscreen changes to auto-exit presentation mode when user exits fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      // If user exits fullscreen while in presentation mode, exit presentation mode
      if (
        !document.fullscreenElement &&
        presentationData?.enabled &&
        excalidrawAPI
      ) {
        console.log("🖥️ 全屏退出，自动退出演示模式");
        const currentAppState = excalidrawAPI.getAppState();
        if (currentAppState.presentationMode?.enabled) {
          const previousState = currentAppState.presentationMode.previousState;
          const frames = excalidrawAPI
            .getSceneElements()
            .filter((e) => e.type === "frame") as ExcalidrawFrameLikeElement[];
          const sortedFrames = sortFramesByPresentationOrder(frames);
          const currentFrame =
            sortedFrames[currentAppState.presentationMode.frameIndex];

          excalidrawAPI.updateScene({
            appState: {
              presentationMode: {
                enabled: false,
                frameIndex: 0,
                previousState: null,
              },
              selectedElementIds: currentFrame
                ? { [currentFrame.id]: true }
                : previousState?.selectedElementIds || {},
              scrollX: previousState?.scrollX ?? currentAppState.scrollX,
              scrollY: previousState?.scrollY ?? currentAppState.scrollY,
              zoom: previousState?.zoom ?? currentAppState.zoom,
              frameRendering:
                previousState?.frameRendering ?? currentAppState.frameRendering,
            },
          });
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [presentationData?.enabled, excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    // Handle presentation mode state - sliding window mode
    if (appState.presentationMode?.enabled && !presentationData?.enabled) {
      // Entering presentation mode - save original scene data and enter fullscreen
      console.log("🎭 进入滑动窗口演示模式:", {
        name: appState.name,
        elementCount: elements.length,
        frameIndex: appState.presentationMode.frameIndex,
      });
      setPresentationData({
        enabled: true,
        elements: elements.filter(
          (el) => !el.isDeleted,
        ) as readonly NonDeletedExcalidrawElement[],
        appState,
        frameIndex: appState.presentationMode.frameIndex,
        originalElements: elements,
        originalAppState: appState,
        originalFiles: files,
      });

      // Enter fullscreen mode
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.warn("Could not enter fullscreen:", err);
        });
      }
      // Don't return - allow normal rendering to continue
    } else if (
      appState.presentationMode?.enabled &&
      presentationData?.enabled
    ) {
      // Already in presentation mode, just update frame index if changed
      if (
        presentationData.frameIndex !== appState.presentationMode.frameIndex
      ) {
        setPresentationData({
          ...presentationData,
          frameIndex: appState.presentationMode.frameIndex,
        });
      }
      // Don't return - allow normal rendering to continue
    } else if (
      !appState.presentationMode?.enabled &&
      presentationData?.enabled
    ) {
      // Exiting presentation mode - restore original data
      console.log("🚪 退出滑动窗口演示模式");

      // Exit fullscreen if in fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.warn("Could not exit fullscreen:", err);
        });
      }

      // Clear presentation data
      setPresentationData(null);
      // Don't return - allow normal rendering to continue
    }

    // console.log('🔄 onChange 触发:', {
    //   elementCount: elements.length,
    //   nonDeletedElements: elements.filter(el => !el.isDeleted).length,
    //   hasFileHandle: !!appState.fileHandle,
    //   fileName: appState.name,
    //   excalidrawAPI: !!excalidrawAPI
    // });

    const nonDeletedElements = elements.filter((el) => !el.isDeleted);

    // 处理有内容的场景
    if (nonDeletedElements.length > 0) {
      // 检查是否是无标题或默认名称，需要智能命名
      const isDefaultName =
        !appState.name ||
        appState.name.startsWith("无标题") ||
        appState.name === "Untitled" ||
        appState.name.match(/^无标题-\d{4}-\d{2}-\d{2}-\d{4}$/) ||
        appState.name.match(/^Untitled-\d{4}-\d{2}-\d{2}-\d{4}$/);

      if (appState.fileHandle) {
        // 有 fileHandle 的文件（通过文件系统打开的文件）
        // 优先使用文件名而不是文件内容中保存的名称
        let fileName = appState.name || "未命名画板";

        // 如果文件名是默认格式但有 fileHandle，尝试从 fileHandle 获取真实文件名
        if (
          isDefaultName &&
          appState.fileHandle &&
          "name" in appState.fileHandle
        ) {
          const fileSystemName = (appState.fileHandle as any).name;
          if (fileSystemName) {
            fileName = fileSystemName.replace(/\.excalidraw$/, "");
            // 更新 appState 中的名称以保持一致性
            if (excalidrawAPI && fileName !== appState.name) {
              excalidrawAPI.updateScene({
                appState: { name: fileName },
              });
            }
          }
        }

        // 如果有 fileHandle，始终优先使用 fileHandle 的名称而不是 appState.name
        // 这确保了每个不同的文件都有正确的唯一文件名
        if (appState.fileHandle && "name" in appState.fileHandle) {
          const fileSystemName = (appState.fileHandle as any).name;
          if (fileSystemName) {
            fileName = fileSystemName.replace(/\.excalidraw$/, "");
          }
        }

        const recentFiles = LocalData.getRecentFiles();
        const existingFile = recentFiles.find((file) => file.name === fileName);

        if (!existingFile) {
          // 记录到最近文件列表
          const timestamp = Date.now();
          const fileInfo = {
            name: fileName,
            lastModified: timestamp,
            isTemporary: false, // 通过文件系统打开的都是正式文件
            description: LocalData.generateTimestampOnlyDescription(timestamp),
          };

          const uniqueId = LocalData.addToRecentFiles(fileInfo);

          // 使用生成的唯一ID保存场景数据
          if (uniqueId) {
            LocalData.saveTemporaryScene(uniqueId, elements, appState, files);
            // 保存该文件的 fileHandle，以便切换文件时恢复
            if (appState.fileHandle) {
              LocalData.saveFileHandleForFile(uniqueId, appState.fileHandle);
            }
          }
        } else {
          // 静默更新已存在的场景数据，使用现有的ID
          LocalData.saveTemporaryScene(
            existingFile.id,
            elements,
            appState,
            files,
          );
          // 更新最后修改时间
          LocalData.updateRecentFileTime(existingFile.id);
          // 保存该文件的 fileHandle，以便切换文件时恢复
          if (appState.fileHandle) {
            LocalData.saveFileHandleForFile(
              existingFile.id,
              appState.fileHandle,
            );
          }
        }
      } else {
        // 没有 fileHandle 的文件（内存中创建的文件）
        if (isDefaultName) {
          // 检查会话中是否已有当前文件的ID
          const sessionFileId = (window as any).__currentTempFileId;

          if (sessionFileId) {
            // 更新已存在的临时文件
            LocalData.updateExistingTemporaryScene(
              sessionFileId,
              elements,
              appState,
              files,
            );
          } else {
            // 创建新的临时文件
            const result = LocalData.saveTemporarySceneWithSequentialName(
              elements,
              appState,
              files,
            );
            // 在会话中记录当前文件ID，避免重复创建
            (window as any).__currentTempFileId = result.id;
          }
        } else {
          // 有意义的文件名，检查是否已存在
          const recentFiles = LocalData.getRecentFiles();
          const existingFile = recentFiles.find(
            (file) => file.name === appState.name,
          );

          if (!existingFile) {
            // 记录到最近文件列表
            const timestamp = Date.now();
            const fileInfo = {
              name: appState.name || "未命名画板",
              lastModified: timestamp,
              isTemporary: false, // 有意义的文件名应该是正式文件，不是临时文件
              description:
                LocalData.generateTimestampOnlyDescription(timestamp),
            };

            const uniqueId = LocalData.addToRecentFiles(fileInfo);

            // 使用生成的唯一ID保存场景数据
            if (uniqueId) {
              LocalData.saveTemporaryScene(uniqueId, elements, appState, files);
            }
          } else {
            // 静默更新已存在的场景数据，使用现有的ID
            LocalData.saveTemporaryScene(
              existingFile.id,
              elements,
              appState,
              files,
            );
          }
        }
      }
    }

    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        window.devicePixelRatio,
        () => forceRefresh((prev) => !prev),
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  const onNewCanvas = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }
    // 当前内容已由 onChange 持续保存到最近文件；重置前把 pending 的防抖保存落盘
    LocalData.flushSave();
    LocalData.flushFileAutoSave();
    // 断开当前临时文件跟踪，之后的绘制会在最近文件中创建新的 画板-XXX
    (window as any).__currentTempFileId = null;
    excalidrawAPI.resetScene({ resetLoadingState: true });
    excalidrawAPI.setToast({
      message: "已新建画布，之前的内容可在「最近文件」中找回",
      duration: 5000,
      closable: true,
    });
  }, [excalidrawAPI]);

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  // Presentation mode now uses sliding window approach - no separate component needed
  // The main Excalidraw component handles the presentation with fullscreen and hidden UI

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <ExportToExcalidrawPlus
                        elements={elements}
                        appState={appState}
                        files={files}
                        name={excalidrawAPI.getName()}
                        onError={(error) => {
                          excalidrawAPI?.updateScene({
                            appState: {
                              errorMessage: error.message,
                            },
                          });
                        }}
                        onSuccess={() => {
                          excalidrawAPI.updateScene({
                            appState: { openDialog: null },
                          });
                        }}
                      />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }
          return (
            <div className="top-right-ui">
              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onNewCanvas={onNewCanvas}
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="collab-offline-warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}
        {excalidrawAPI && <DuplicationHandler excalidrawAPI={excalidrawAPI} />}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: "新建画布 (New Canvas)",
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              keywords: [
                "new",
                "canvas",
                "blank",
                "create",
                "file",
                "xinjian",
                "huabu",
              ],
              perform: () => {
                onNewCanvas();
              },
            },
            {
              label: "New Window",
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              keywords: [
                "new",
                "window",
                "tab",
                "independent",
                "separate",
                "session",
              ],
              perform: () => {
                sessionManager.openNewSession();
              },
            },
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            ...(isExcalidrawPlusSignedUser
              ? [
                  {
                    ...ExcalidrawPlusAppCommand,
                    label: "Sign in / Go to Excalidraw+",
                  },
                ]
              : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

            {
              label: t("overwriteConfirm.action.excalidrawPlus.button"),
              category: DEFAULT_CATEGORIES.export,
              icon: exportToPlus,
              predicate: true,
              keywords: ["plus", "export", "save", "backup"],
              perform: () => {
                if (excalidrawAPI) {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";

  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
